import { PropsWithChildren, ReactNode } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Link } from '@treams/treams-routes';

import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import { SettingsRow } from 'packages/kakapo-ui-kit/components/common/SettingsTable';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';

const Anchor = styled(Link)`
	color: ${(props) => props.theme.colors.primary};

	&:hover {
		text-decoration: underline;

		&:active {
			color: black;
		}
	}
`;

export default function SettingsItem<T extends { id: string; isArchived: boolean }>(
	props: PropsWithChildren<{
		item: T;
		accessory?: ReactNode;
		link?: (item: T) => string;
		title: (item: T) => string;
		archive?: [
			(options: { variables: { input: { id: string } } }) => Promise<void>,
			{ loading: boolean },
		];
		restore?: [
			(options: { variables: { input: { id: string } } }) => Promise<void>,
			{ loading: boolean },
		];
		loading?: boolean;
		onEdit?: () => void;
		onArchive?: () => void;
		onRefresh: () => void;
	}>,
) {
	const isArchived = !!props.item?.isArchived;

	const [error, attempt] = useErrorBoundary();

	const onRestore =
		props.restore &&
		attempt(async () => {
			await props.restore?.[0]({
				variables: {
					input: {
						id: props.item!.id,
					},
				},
			});

			props.onRefresh();
		});

	const onDelete =
		props.archive &&
		attempt(async () => {
			await props.archive?.[0]({
				variables: {
					input: {
						id: props.item!.id,
					},
				},
			});

			props.onRefresh();
		});

	return (
		<SettingsRow
			loading={props.archive?.[1].loading || props.restore?.[1].loading || props.loading}
			onEdit={props.onEdit}
			onRestore={isArchived ? onRestore : undefined}
			onDelete={props.onArchive ?? (!isArchived ? onDelete : undefined)}
		>
			<GraphQLErrorMessage error={error} />
			<Flex alignItems="center">
				<Box flexGrow={1} flexShrink={1}>
					{props.link ? (
						<Anchor to={props.link(props.item)}>{props.title(props.item)}</Anchor>
					) : (
						props.title(props.item)
					)}
				</Box>
				{props.accessory && (
					<Box flexGrow={0} flexShrink={0}>
						{props.accessory}
					</Box>
				)}
			</Flex>
			{props.children}
		</SettingsRow>
	);
}
